import { Button, Flex, Text, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Label } from '../../../../components/formComponents';
import Input from '../../../../components/formComponents/Input';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { METHODFI_FLOW_VARIATIONS } from '../../../../constants/methodfi';
import {
  useCreateMethodFiIndividualEntityMutation,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { useTempInfo } from '../../../../hooks';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import { phoneValidationRequired } from '../../../../utils/validation/phoneNumber';
import MethodFiConsentCopy from './MethodFiConsentCopy';
import useMethodFiConsent from './useMethodFiConsent';

type AutoFillPanelProps = {
  onSuccess: (entityId: string) => void;
  onSkip: () => void;
};

type FormValues = {
  first_name: string;
  last_name: string;
  phone_number: string;
};

const validationSchema = Yup.object({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  phone_number: phoneValidationRequired,
});

const AutoFillPanel = ({ onSuccess, onSkip }: AutoFillPanelProps) => {
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const { info } = useTempInfo({ fetchPolicy: 'network-only' });
  const tempInfoData = info?.data;
  const { consentText, consentLoading, saveConsent } = useMethodFiConsent();
  const immediateOptOut =
    tempInfoData?.methodfi_flow_variation === METHODFI_FLOW_VARIATIONS.immediateSkip;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [createIndividualEntity] = useCreateMethodFiIndividualEntityMutation();

  const handleSubmit = async (values: FormValues) => {
    await saveConsent();

    await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            phone_number: values.phone_number,
            first_name: values.first_name,
            last_name: values.last_name,
          },
        },
      },
    });

    const entityResponse = await createIndividualEntity({
      variables: {
        phoneNumber: values.phone_number,
        firstName: values.first_name,
        lastName: values.last_name,
      },
    });

    const entityId = entityResponse.data?.createIndividualEntity?.id;
    if (!entityId) {
      return;
    }

    onSuccess(entityId);
  };

  return (
    <Formik<FormValues>
      initialValues={{
        first_name: tempInfoData?.first_name ?? '',
        last_name: tempInfoData?.last_name ?? '',
        phone_number: tempInfoData?.phone_number ?? '',
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <Form>
          <VStack spacing={4} align="stretch">
            <Text textAlign="center">
              We'll send you a one-time code. Once you're confirmed, we'll securely pre-fill parts
              of your application with available information to save you time on the details.
            </Text>

            <Flex direction="column">
              <Label>CONFIRM YOUR NAME</Label>
              <Flex gap={2} mt={1} flexDir={{ base: 'column', md: 'row' }}>
                <Input name="first_name" placeholder="First Name" _container={{ flex: 1 }} />
                <Input name="last_name" placeholder="Last Name" _container={{ flex: 1 }} />
              </Flex>
            </Flex>

            <Input
              label="PHONE NUMBER"
              name="phone_number"
              placeholder="Phone Number"
              mask="(999) 999-9999"
              type="tel"
              _container={{ w: '100%' }}
            />

            <MethodFiConsentCopy consentText={consentText} />

            <VStack spacing={3} mt={4}>
              <PrimaryButton type="submit" isLoading={isSubmitting || consentLoading} w="full">
                VERIFY PHONE NUMBER
              </PrimaryButton>
              {immediateOptOut && (
                <Button onClick={onSkip} variant="link">
                  Skip Phone Verification
                </Button>
              )}
            </VStack>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export default AutoFillPanel;
